import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { AUTO_SAVE_INTERVAL_MS } from "@/lib/constants";
import { formatFieldValue, formatFormData, formatTimeIST } from "@/lib/formatUtils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, Lock, FileEdit, ChevronDown, ChevronUp, PackageCheck, AlertTriangle, FileCheck, FileWarning, Truck } from "lucide-react";
import DraftSection from "@/components/shared/DraftSection";
import ExportButtons from "@/components/shared/ExportButtons";
import EntryDetailDialog from "@/components/shared/EntryDetailDialog";
import logger from "@/lib/logger";
import { MaterialEntryForm, ChallanDialog, ReceiveJobWorkDialog } from "./material/Dialogs";
import CylinderForm from "./material/CylinderForm";
import OtherValueDialog from "@/components/shared/OtherValueDialog";

const TAB_CONFIG = {
  rgp: { api: "/mod2/rgp", prefix: "RGP", label: "RGP (Returnable)" },
  nrgp: { api: "/mod2/nrgp", prefix: "NRGP", label: "NRGP (Non-Returnable)" },
  jobwork: { api: "/mod2/jobwork", prefix: "JW", label: "Job Work" },
  rework: { api: "/mod2/rework", prefix: "RW", label: "Rework" },
  cylinder: { api: "/mod2/cylinder", prefix: "CYL", label: "Cylinder" },
  custjw: { api: "/mod2/customer-jobwork", prefix: "CJW", label: "Customer JW (Inward)" },
};

const REPORT_TYPE_MAP = {
  rgp: "rgp_register",
  nrgp: "nrgp_register",
  jobwork: "jobwork_register",
  rework: "rework_register",
  cylinder: "cylinder_register",
  custjw: "customer_jw_register",
};

const COLLECTION_MAP = {
  rgp: "rgp_entries",
  nrgp: "nrgp_entries",
  jobwork: "jobwork_entries",
  rework: "rework_entries",
  cylinder: "cylinder_entries",
  custjw: "customer_jobwork_entries",
};

export default function MaterialMovement() {
  const [tab, setTab] = useState("rgp");
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [detailEntry, setDetailEntry] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [parties, setParties] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [challanLog, setChallanLog] = useState([]);
  const [showChallan, setShowChallan] = useState(false);
  const [challanForm, setChallanForm] = useState({});
  const [challanEntryId, setChallanEntryId] = useState(null);
  const [otherField, setOtherField] = useState(null);
  const [otherValue, setOtherValue] = useState("");
  const [showReceive, setShowReceive] = useState(false);
  const [receiveEntryId, setReceiveEntryId] = useState(null);
  const [receiveEntry, setReceiveEntry] = useState(null);
  const [receiveForm, setReceiveForm] = useState({});
  const [jwInvoiceDialog, setJwInvoiceDialog] = useState(null);
  const [dispatchCJW, setDispatchCJW] = useState(null);
  const [dispatchForm, setDispatchForm] = useState({});
  const [jwInvoiceForm, setJwInvoiceForm] = useState({});
  const autoSaveRef = useRef(null);
  const { location } = useAuth();

  const cfg = TAB_CONFIG[tab];

  const exportColumns = useMemo(() => [
    {key:"ref_no",label:"Ref"},{key:"party_name",label:"Party"},{key:"process_type",label:"Process"},{key:"material_description",label:"Material"},{key:"quantity",label:"Qty"},{key:"status",label:"Status"},
  ], []);

  const fetchEntries = useCallback(async () => {
    try {
      const { data } = await api.get(cfg.api, { params: { location, search: search || undefined } });
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (err) { logger.error("Failed to fetch entries:", err); }
  }, [tab, location, search, cfg.api]);

  const fetchDrafts = useCallback(async () => {
    try {
      const { data } = await api.get(`/drafts/${tab}`, { params: { location } });
      setDrafts(data.drafts || []);
    } catch (err) { logger.error("Failed to fetch drafts:", err); }
  }, [tab, location]);

  useEffect(() => { fetchEntries(); fetchDrafts(); }, [fetchEntries, fetchDrafts]);

  const loadMasters = useCallback(async () => {
    try {
      const [p, pt] = await Promise.all([api.get("/masters/parties"), api.get("/masters/process_types")]);
      setParties(p.data.items);
      setProcesses(pt.data.items);
    } catch (err) { logger.error("Failed to load masters:", err); }
  }, []);

  useEffect(() => { loadMasters(); }, [loadMasters]);

  // Auto-save — use refs to avoid interval recreation on every keystroke
  const formRef = useRef(form);
  formRef.current = form;
  const draftIdRef = useRef(currentDraftId);
  draftIdRef.current = currentDraftId;

  useEffect(() => {
    if (!showForm) return;
    autoSaveRef.current = setInterval(async () => {
      const f = formRef.current;
      if (!f || Object.keys(f).length === 0) return;
      try {
        const payload = { ...f, location, _draft_id: draftIdRef.current || undefined };
        const { data } = await api.post(`/drafts/${tab}`, payload);
        if (!draftIdRef.current && data.id) setCurrentDraftId(data.id);
        setLastAutoSave(new Date());
      } catch (err) { logger.error("Auto-save failed:", err); }
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(autoSaveRef.current);
  }, [showForm, tab, location]);

  const handleSaveDraft = async () => {
    try {
      const payload = { ...form, location, _draft_id: currentDraftId || undefined };
      const { data } = await api.post(`/drafts/${tab}`, payload);
      if (!currentDraftId && data.id) setCurrentDraftId(data.id);
      toast.success("Draft saved");
      setLastAutoSave(new Date());
      fetchDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleSubmit = async () => {
    // Cylinder-specific validation
    if (tab === "cylinder") {
      if (!form.gas_type?.trim()) { toast.error("Gas Type is required"); return; }
      if (!form.party_name?.trim()) { toast.error("Party / Supplier is required"); return; }
      const cyls = form.cylinders || [];
      if (cyls.length === 0) { toast.error("Add at least one cylinder"); return; }
      const hasWeights = cyls.some(c => (parseFloat(c.full_weight) || 0) > 0);
      if (!hasWeights) { toast.error("Enter weight for at least one cylinder"); return; }
    } else {
      // Standard material validation
      if (!form.party_name?.trim()) { toast.error("Party Name is required"); return; }
      if (!form.material_description?.trim()) { toast.error("Material Description is required"); return; }
      if (!form.quantity?.toString().trim()) { toast.error("Quantity is required"); return; }
      if ((tab === "rgp" || tab === "nrgp") && !form.erp_challan_no?.trim()) { toast.error("Challan / Invoice No. is required"); return; }
      if (tab === "rgp" && !form.tentative_return_date?.trim()) { toast.error("Tentative Return Date is required for RGP"); return; }
    }
    setSaving(true);
    try {
      const payload = tab === "cylinder"
        ? { ...form, location }
        : { ...formatFormData(form), location };
      await api.post(cfg.api, payload);
      toast.success(`${cfg.prefix} entry created`);
      if (currentDraftId) await api.post(`/drafts/${tab}/${currentDraftId}/submit`).catch(() => {});
      closeForm();
      fetchEntries();
      fetchDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const handleCloseRGP = async (id) => {
    try {
      await api.put(`/mod2/rgp/${id}/close`, {});
      toast.success("RGP closed");
      fetchEntries();
    } catch { toast.error("Failed"); }
  };

  const handleReceiveJobWork = async () => {
    if (!receiveEntryId) return;
    // Route to the right endpoint based on current tab — RGP and Job Work share the dialog
    const endpoint = tab === "rgp"
      ? `/mod2/rgp/${receiveEntryId}/receive`
      : `/mod2/jobwork/${receiveEntryId}/receive`;
    try {
      const { data } = await api.put(endpoint, receiveForm);
      toast.success(data.message || "Receipt recorded");
      setShowReceive(false);
      setReceiveEntryId(null);
      setReceiveEntry(null);
      setReceiveForm({});
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to receive"); }
  };

  const getDaysOpen = (createdAt) => {
    if (!createdAt) return 0;
    const diff = Date.now() - new Date(createdAt).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  const handleOpenDraft = async (draft) => {
    try {
      const { data } = await api.get(`/drafts/${tab}/${draft.id}`);
      setForm(data.data || {});
      setCurrentDraftId(draft.id);
      setShowForm(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Cannot open draft"); }
  };

  const handleDeleteDraft = async (draftId) => {
    try {
      await api.delete(`/drafts/${tab}/${draftId}`);
      toast.success("Draft deleted");
      fetchDrafts();
    } catch { toast.error("Failed"); }
  };

  const toggleChallanLog = async (entryId) => {
    if (expandedRow === entryId) {
      setExpandedRow(null);
      return;
    }
    try {
      const entryType = tab === "rgp" ? "rgp" : "nrgp";
      const { data } = await api.get(`/mod2/${entryType}/${entryId}/challan`);
      setChallanLog(data.challan_log || []);
      setExpandedRow(entryId);
    } catch { toast.error("Failed to load challans"); }
  };

  const handleAddChallan = async () => {
    if (!challanEntryId) return;
    try {
      const entryType = tab === "rgp" ? "rgp" : "nrgp";
      await api.post(`/mod2/${entryType}/${challanEntryId}/challan`, challanForm);
      toast.success("Challan added");
      setChallanForm({});
      setShowChallan(false);
      toggleChallanLog(challanEntryId);
    } catch { toast.error("Failed"); }
  };

  const fetchMasters = () => {
    Promise.all([api.get("/masters/parties"), api.get("/masters/process_types")]).then(([p, pt]) => {
      setParties(p.data.items);
      setProcesses(pt.data.items);
    }).catch((err) => { logger.error("Failed to refresh masters:", err); });
  };

  const handleOtherSubmit = async () => {
    if (!otherValue.trim() || !otherField) return;
    const val = formatFieldValue(otherField.fieldKey, otherValue.trim());
    setField(otherField.fieldKey, val);
    try { await api.post(`/masters/${otherField.masterType}`, { name: val }); } catch (err) { logger.error("Failed to save master:", err); }
    fetchMasters();
    toast.success(`"${val}" added`);
    setOtherField(null);
    setOtherValue("");
  };

  const handleMarkJwInvoice = async () => {
    if (!jwInvoiceDialog || !jwInvoiceForm.invoice_no?.trim()) { toast.error("Invoice No. is required"); return; }
    try {
      const coll = COLLECTION_MAP[tab] || "jobwork_entries";
      await api.put(`/invoice/mark-received/${coll}/${jwInvoiceDialog.id}`, jwInvoiceForm);
      toast.success("Invoice marked as received");
      setJwInvoiceDialog(null);
      setJwInvoiceForm({});
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDispatchCustJW = (entry) => {
    setDispatchCJW(entry);
    // Pre-populate with REMAINING balance (handles partial-dispatch flow correctly)
    const prevDispatched = (entry.dispatches || []).reduce((s, r) => s + (parseFloat(r.dispatch_qty) || 0), 0);
    const remaining = Math.max(0, (parseFloat(entry.quantity) || 0) - prevDispatched);
    setDispatchForm({
      dispatch_date: new Date().toISOString().slice(0, 10),
      dispatch_qty: remaining || "",
      dispatch_uom: entry.uom || "Kg",
      ojr_no: "",
      dispatch_vehicle_no: "",
      amount: "",
      remarks: "",
    });
  };

  const submitDispatchCustJW = async () => {
    if (!dispatchCJW?.id) return;
    if (!dispatchForm.ojr_no?.trim()) { toast.error("OJR / Dispatch Challan No. is required"); return; }
    if (!(parseFloat(dispatchForm.dispatch_qty) > 0)) { toast.error("Dispatch quantity must be greater than 0"); return; }
    try {
      const { data } = await api.put(`/mod2/customer-jobwork/${dispatchCJW.id}/dispatch`, dispatchForm);
      toast.success(data.message || "Dispatched to customer");
      setDispatchCJW(null);
      setDispatchForm({});
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to dispatch"); }
  };

  const closeForm = () => {
    setShowForm(false);
    if (currentDraftId) api.post(`/drafts/${tab}/${currentDraftId}/release-lock`).catch(() => {});
    setCurrentDraftId(null);
    setLastAutoSave(null);
    setForm({});
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: formatFieldValue(k, v) }));
  const handleSelectOrOther = (fieldKey, val, masterType) => {
    if (val === "__other__") { setOtherField({ masterType, fieldKey }); return; }
    setField(fieldKey, val);
  };

  const showChallanColumn = tab === "rgp" || tab === "nrgp";
  const isCylinderTab = tab === "cylinder";

  return (
    <div className="space-y-5" data-testid="material-movement-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Chivo" }}>Material Movement</h1>
          <p className="text-xs sm:text-sm text-gray-500">MOD-2 · RGP, NRGP, Job Work, Rework, Cylinder, Customer JW</p>
        </div>
        <Button onClick={() => { setForm({}); setCurrentDraftId(null); setShowForm(true); }} className="bg-[#1B3C87] hover:bg-[#2650A8] h-10 sm:h-11 text-xs sm:text-sm w-full sm:w-auto" data-testid="new-entry-btn">
          <Plus size={18} className="mr-1" /> New {cfg.prefix}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setSearch(""); setExpandedRow(null); }}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between flex-wrap gap-2">
          <TabsList className="h-10 sm:h-11 bg-[#1B3C87]/5 flex-wrap">
            {Object.entries(TAB_CONFIG).map(([k, v]) => (
              <TabsTrigger key={k} value={k} className="h-8 sm:h-9 px-3 sm:px-4 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid={`tab-${k}`}>
                {v.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="relative w-full sm:w-auto">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 w-full sm:w-52" data-testid="search-input" />
          </div>
        </div>

        <div className="flex items-center mt-3">
          <ExportButtons
            reportType={REPORT_TYPE_MAP[tab]}
            title={cfg.label}
            entries={entries}
            columns={exportColumns}
            location={location}
          />
        </div>

        <DraftSection drafts={drafts} onOpen={handleOpenDraft} onDelete={handleDeleteDraft} draftType={tab} />

        {Object.keys(TAB_CONFIG).map(tabKey => (
          <TabsContent key={tabKey} value={tabKey}>
            <Card className="mt-3">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
                    <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Ref No.</TableHead>
                    {tabKey === "cylinder" ? (
                      <>
                        <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Gas Type</TableHead>
                        <TableHead className="text-white text-xs uppercase tracking-wider font-bold">RGP/NRGP</TableHead>
                        <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Party</TableHead>
                        <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Cylinders</TableHead>
                        <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Gas Weight (Kg)</TableHead>
                      </>
                    ) : tabKey === "custjw" ? (
                      <>
                        <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Customer</TableHead>
                        <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Material</TableHead>
                        <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Qty</TableHead>
                        <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Process</TableHead>
                        <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Doc Type</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Party Name</TableHead>
                        {tabKey === "jobwork" && <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Process</TableHead>}
                        <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Material</TableHead>
                        <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Qty</TableHead>
                        {tabKey === "jobwork" && <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Direction</TableHead>}
                      </>
                    )}
                    <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Status</TableHead>
                    <TableHead className="text-white text-xs uppercase tracking-wider font-bold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400">No entries</TableCell></TableRow>}
                  {entries.map(e => (
                    <React.Fragment key={e.id}>
                      <TableRow key={e.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                        <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                        {tabKey === "cylinder" ? (
                          <>
                            <TableCell className="text-sm font-medium">{e.gas_type || "—"}</TableCell>
                            <TableCell className="text-sm"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${e.movement_type === "RGP" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`}>{e.movement_type || "—"}</span></TableCell>
                            <TableCell className="text-sm">{e.party_name || "—"}</TableCell>
                            <TableCell className="text-sm font-semibold">{e.total_cylinders || 0}</TableCell>
                            <TableCell className="text-sm font-semibold text-emerald-700">{e.total_gas_weight || 0} Kg</TableCell>
                          </>
                        ) : tabKey === "custjw" ? (
                          <>
                            <TableCell className="font-medium">{e.customer_name || e.party_name || "—"}</TableCell>
                            <TableCell className="text-sm">{e.material_description || "—"}</TableCell>
                            <TableCell className="text-sm">{e.quantity || "—"}{e.uom ? ` ${e.uom}` : ""}</TableCell>
                            <TableCell className="text-sm">{e.process_type || "—"}</TableCell>
                            <TableCell>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${e.doc_type === "Challan" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                                {e.doc_type || "Invoice"}
                              </span>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="font-medium">{e.party_name || "—"}</TableCell>
                            {tabKey === "jobwork" && <TableCell className="text-sm">{e.process_type || "—"}</TableCell>}
                            <TableCell className="text-sm">{e.material_description || "—"}</TableCell>
                            <TableCell className="text-sm">{e.quantity || "—"}{e.uom ? ` ${e.uom}` : ""}</TableCell>
                            {tabKey === "jobwork" && <TableCell className="text-sm capitalize">{e.direction || "—"}</TableCell>}
                          </>
                        )}
                        <TableCell><span className={`status-${e.status} px-2 py-0.5 rounded text-xs font-semibold`}>{e.status}</span></TableCell>
                        <TableCell>
                          <div className="flex gap-1 items-center">
                            {tabKey === "rgp" && (e.status === "open" || e.status === "partial") && (
                              <>
                                <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={(ev) => { ev.stopPropagation(); setReceiveEntryId(e.id); setReceiveEntry(e); setReceiveForm({ date_of_receipt: new Date().toISOString().slice(0, 10) }); setShowReceive(true); }} data-testid={`receive-rgp-${e.id}`}>
                                  <PackageCheck size={12} className="mr-1" /> Receive
                                </Button>
                                {e.status === "partial" && (
                                  <span className="text-[10px] text-blue-600 font-semibold whitespace-nowrap" data-testid={`rgp-progress-${e.id}`}>
                                    {e.total_received || 0}/{e.quantity}
                                    {e.balance_qty != null && <span className="text-amber-600 ml-1">(bal {e.balance_qty})</span>}
                                  </span>
                                )}
                                <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={(ev) => { ev.stopPropagation(); handleCloseRGP(e.id); }} data-testid={`close-rgp-${e.id}`}>
                                  <Lock size={12} className="mr-1" /> Close
                                </Button>
                              </>
                            )}
                            {tabKey === "jobwork" && (e.status === "open" || e.status === "overdue" || e.status === "partial") && (
                              <>
                                <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => { setReceiveEntryId(e.id); setReceiveEntry(e); setReceiveForm({}); setShowReceive(true); }} data-testid={`receive-jw-${e.id}`}>
                                  <PackageCheck size={12} className="mr-1" /> Receive
                                </Button>
                                {e.status === "partial" && (
                                  <span className="text-[10px] text-blue-600 font-semibold">{e.total_received || 0}/{e.quantity}</span>
                                )}
                                {getDaysOpen(e.created_at) > 5 && e.status !== "partial" && (
                                  <span className="flex items-center gap-0.5 text-[10px] text-red-600 font-semibold" title="Overdue - past 5 days">
                                    <AlertTriangle size={12} /> Overdue
                                  </span>
                                )}
                              </>
                            )}
                            {tabKey === "jobwork" && e.doc_type === "Challan" && e.invoice_status === "pending" && (
                              <Button size="sm" className="h-7 text-[10px] bg-amber-600 hover:bg-amber-700" onClick={(ev) => { ev.stopPropagation(); setJwInvoiceDialog(e); setJwInvoiceForm({ invoice_received_date: new Date().toISOString().slice(0, 10) }); }} data-testid={`mark-jw-invoice-${e.id}`}>
                                <FileWarning size={12} className="mr-1" /> Mark Invoice
                              </Button>
                            )}
                            {tabKey === "jobwork" && e.doc_type === "Challan" && e.invoice_status === "received" && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 flex items-center gap-1 whitespace-nowrap"><FileCheck size={10} /> Inv. Rcvd</span>
                            )}
                            {tabKey === "custjw" && (e.status === "in_process" || e.status === "partial_dispatch") && (
                              <>
                                <Button size="sm" className="h-7 text-[10px] bg-blue-600 hover:bg-blue-700" onClick={(ev) => { ev.stopPropagation(); handleDispatchCustJW(e); }} data-testid={`dispatch-cjw-${e.id}`}>
                                  <Truck size={12} className="mr-1" /> Dispatch
                                </Button>
                                {e.status === "partial_dispatch" && (
                                  <span className="text-[10px] text-blue-700 font-semibold whitespace-nowrap" data-testid={`cjw-progress-${e.id}`}>
                                    {e.total_dispatched || 0}/{e.quantity}
                                    {e.balance_to_dispatch != null && <span className="text-amber-600 ml-1">(bal {e.balance_to_dispatch})</span>}
                                  </span>
                                )}
                              </>
                            )}
                            {tabKey === "custjw" && e.doc_type === "Challan" && e.invoice_status === "pending" && (
                              <Button size="sm" className="h-7 text-[10px] bg-amber-600 hover:bg-amber-700" onClick={(ev) => { ev.stopPropagation(); setJwInvoiceDialog(e); setJwInvoiceForm({ invoice_received_date: new Date().toISOString().slice(0, 10) }); }} data-testid={`mark-cjw-invoice-${e.id}`}>
                                <FileWarning size={12} className="mr-1" /> Mark Invoice
                              </Button>
                            )}
                            {tabKey === "custjw" && e.doc_type === "Challan" && e.invoice_status === "received" && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 flex items-center gap-1 whitespace-nowrap"><FileCheck size={10} /> Inv. Rcvd</span>
                            )}
                            {showChallanColumn && (
                              <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => toggleChallanLog(e.id)} data-testid={`toggle-challan-${e.id}`}>
                                {expandedRow === e.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                Challans
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {/* Challan Log Sub-view */}
                      {showChallanColumn && expandedRow === e.id && (
                        <TableRow key={`challan-${e.id}`}>
                          <TableCell colSpan={8} className="bg-gray-50 p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-sm font-bold text-gray-700">Challan Log — {e.ref_no}</h4>
                              <Button size="sm" className="h-7 text-[10px] bg-[#1B3C87]" onClick={() => { setChallanEntryId(e.id); setChallanForm({}); setShowChallan(true); }}>
                                <Plus size={12} className="mr-1" /> Add Challan
                              </Button>
                            </div>
                            {challanLog.length === 0 ? (
                              <p className="text-sm text-gray-400">No challans recorded yet</p>
                            ) : (
                              <div className="space-y-2">
                                {challanLog.map((c) => (
                                  <div key={c.challan_no || c.challan_date} className="flex items-center gap-4 p-2 bg-white rounded border text-sm">
                                    <span className="font-mono text-xs font-bold">{c.challan_no}</span>
                                    <span className="text-gray-500">{c.challan_date}</span>
                                    <span className="flex-1">{c.description}</span>
                                    <span className="text-gray-400 text-xs">{c.quantity}</span>
                                    <span className="text-gray-400 text-xs">by {c.added_by?.split("@")[0]}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* New Entry Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) closeForm(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle style={{ fontFamily: "Chivo" }}>
                {currentDraftId ? <span className="flex items-center gap-2"><FileEdit size={16} className="text-amber-600" /> Continue Draft</span> : `New ${cfg.label}`}
              </DialogTitle>
              {lastAutoSave && <span className="text-[10px] text-gray-400">Draft saved {formatTimeIST(lastAutoSave)}</span>}
            </div>
          </DialogHeader>
          {tab === "cylinder" ? (
            <CylinderForm form={form} setField={setField} parties={parties} onSelectOrOther={handleSelectOrOther} />
          ) : (
            <MaterialEntryForm tab={tab} form={form} setField={setField} parties={parties} processes={processes} onSelectOrOther={handleSelectOrOther} />
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={closeForm} className="h-11">Cancel</Button>
            <Button variant="outline" onClick={handleSaveDraft} className="h-11 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="save-draft-btn">
              <FileEdit size={14} className="mr-1" /> Save Draft
            </Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1B3C87] hover:bg-[#2650A8] h-11" data-testid="save-entry-btn">
              {saving ? "Saving..." : "Submit Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChallanDialog open={showChallan} onClose={setShowChallan} challanForm={challanForm} setChallanForm={setChallanForm} onSubmit={handleAddChallan} />

      <ReceiveJobWorkDialog open={showReceive} onClose={setShowReceive} receiveForm={receiveForm} setReceiveForm={setReceiveForm} onSubmit={handleReceiveJobWork} entry={receiveEntry} mode={tab === "rgp" ? "rgp" : "jobwork"} />

      {/* "Other" value dialog */}
      <OtherValueDialog
        otherField={otherField}
        otherValue={otherValue}
        setOtherValue={setOtherValue}
        onSubmit={handleOtherSubmit}
        onClose={() => { setOtherField(null); setOtherValue(""); }}
      />

      <EntryDetailDialog open={!!detailEntry} onClose={() => setDetailEntry(null)} entry={detailEntry} title="Entry Details" collection={COLLECTION_MAP[tab] || "rgp_entries"} onRefresh={fetchEntries} />

      {/* Job Work Mark Invoice Received Dialog */}
      <Dialog open={!!jwInvoiceDialog} onOpenChange={(open) => { if (!open) { setJwInvoiceDialog(null); setJwInvoiceForm({}); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo" }}>
              <span className="flex items-center gap-2"><FileCheck size={18} className="text-emerald-600" /> Mark Invoice Received</span>
            </DialogTitle>
          </DialogHeader>
          {jwInvoiceDialog && (
            <div className="bg-slate-50 border rounded-lg p-3 space-y-1 text-sm mb-2">
              <div className="flex justify-between"><span className="text-gray-500">Ref:</span><span className="font-mono font-bold">{jwInvoiceDialog.ref_no}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Party:</span><span className="font-medium">{jwInvoiceDialog.party_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Material:</span><span>{jwInvoiceDialog.material_description}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Challan No.:</span><span className="font-medium">{jwInvoiceDialog.erp_challan_no || "—"}</span></div>
            </div>
          )}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Invoice No. *</Label>
              <Input value={jwInvoiceForm.invoice_no || ""} onChange={e => setJwInvoiceForm(p => ({ ...p, invoice_no: e.target.value }))} className="h-11" placeholder="Enter invoice number" data-testid="mark-jw-invoice-no" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Invoice Received Date</Label>
              <Input type="date" value={jwInvoiceForm.invoice_received_date || ""} onChange={e => setJwInvoiceForm(p => ({ ...p, invoice_received_date: e.target.value }))} className="h-11" data-testid="mark-jw-invoice-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setJwInvoiceDialog(null); setJwInvoiceForm({}); }}>Cancel</Button>
            <Button onClick={handleMarkJwInvoice} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-mark-jw-invoice-btn">
              <FileCheck size={14} className="mr-1" /> Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer JW Dispatch Dialog — capture OJR, qty, vehicle, amount before closing */}
      <Dialog open={!!dispatchCJW} onOpenChange={(o) => { if (!o) { setDispatchCJW(null); setDispatchForm({}); } }}>
        <DialogContent className="max-w-lg" data-testid="dispatch-cjw-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo" }} className="flex items-center gap-2">
              <Truck size={18} className="text-blue-600" /> Dispatch to Customer
            </DialogTitle>
          </DialogHeader>
          {dispatchCJW && (() => {
            const prevDispatched = (dispatchCJW.dispatches || []).reduce((s, r) => s + (parseFloat(r.dispatch_qty) || 0), 0);
            const remaining = Math.max(0, (parseFloat(dispatchCJW.quantity) || 0) - prevDispatched);
            const isPartial = dispatchCJW.status === "partial_dispatch";
            return (
            <>
              <div className="bg-slate-50 rounded p-3 text-xs space-y-1 border">
                <div className="flex justify-between"><span className="text-gray-500">Customer:</span><strong>{dispatchCJW.customer_name || dispatchCJW.party_name}</strong></div>
                <div className="flex justify-between"><span className="text-gray-500">Material:</span><span>{dispatchCJW.material_description}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Inward Qty:</span><span className="font-mono">{dispatchCJW.quantity} {dispatchCJW.uom}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Inward Challan:</span><span className="font-mono">{dispatchCJW.challan_no || "—"}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Process:</span><span>{dispatchCJW.process_type || "—"}</span></div>
                {isPartial && (
                  <>
                    <div className="flex justify-between text-blue-700"><span>Already Dispatched:</span><span className="font-mono font-bold">{prevDispatched} {dispatchCJW.uom}</span></div>
                    <div className="flex justify-between text-amber-700"><span>Balance to Dispatch:</span><span className="font-mono font-bold" data-testid="dispatch-balance">{remaining} {dispatchCJW.uom}</span></div>
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Dispatch Date *</Label>
                  <Input type="date" value={dispatchForm.dispatch_date || ""} onChange={(e) => setDispatchForm((p) => ({ ...p, dispatch_date: e.target.value }))} className="h-11" data-testid="dispatch-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">OJR / Dispatch Challan No. *</Label>
                  <Input value={dispatchForm.ojr_no || ""} onChange={(e) => setDispatchForm((p) => ({ ...p, ojr_no: e.target.value }))} className="h-11" placeholder="e.g. OJR-1234" data-testid="dispatch-ojr-no" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Dispatch Qty *</Label>
                  <Input type="number" step="0.01" value={dispatchForm.dispatch_qty || ""} onChange={(e) => setDispatchForm((p) => ({ ...p, dispatch_qty: e.target.value }))} className="h-11" data-testid="dispatch-qty" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">UOM</Label>
                  <Input value={dispatchForm.dispatch_uom || ""} onChange={(e) => setDispatchForm((p) => ({ ...p, dispatch_uom: e.target.value }))} className="h-11" placeholder="Kg / Nos" data-testid="dispatch-uom" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle No.</Label>
                  <Input value={dispatchForm.dispatch_vehicle_no || ""} onChange={(e) => setDispatchForm((p) => ({ ...p, dispatch_vehicle_no: e.target.value.toUpperCase() }))} className="h-11" placeholder="e.g. PB08AB1234" data-testid="dispatch-vehicle" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Amount (Rs.)</Label>
                  <Input type="number" step="0.01" value={dispatchForm.amount || ""} onChange={(e) => setDispatchForm((p) => ({ ...p, amount: e.target.value }))} className="h-11" placeholder="Job work charges" data-testid="dispatch-amount" />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Remarks</Label>
                  <Input value={dispatchForm.remarks || ""} onChange={(e) => setDispatchForm((p) => ({ ...p, remarks: e.target.value }))} className="h-11" data-testid="dispatch-remarks" />
                </div>
              </div>
            </>
          ); })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDispatchCJW(null); setDispatchForm({}); }} className="h-11">Cancel</Button>
            <Button onClick={submitDispatchCustJW} className="bg-blue-600 hover:bg-blue-700 h-11" data-testid="confirm-dispatch-cjw-btn">
              <Truck size={14} className="mr-1" /> Confirm Dispatch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
